import { AxiosResponse } from 'axios'
import { useMutation } from 'react-query'
import { axiosInstance } from '../Services'

export type ImageType =
  | 'BRAND_LOGO'
  | 'BRAND_COVER'
  | 'AD_IMAGE'
  | 'OFFER_IMAGE'
  | 'BRAND_STORY_IMAGE'
  | 'WRAPPER_IMAGE'
  | 'GOOGLE_WALLET_LOGO'
  | 'APPLE_WALLET_LOGO'
  | 'GOOGLE_WALLET_IMAGE'
  | 'APPLE_WALLET_IMAGE'

export interface SuccessfulUpload {
  jpgUrl: string
  webpUrl: string
  path: string
  imagePathCompressedAsJpeg?: string
}

export interface FailedUpload {
  index: number
  fileName: string
  error: string
  errorCode: string
}

export interface BulkImageUploadResponse {
  totalRequested: number
  successCount: number
  failureCount: number
  successfulUploads: SuccessfulUpload[]
  failedUploads: FailedUpload[]
}

export interface BulkImageUploadRequest {
  brandId: string
  type: ImageType
  files: File[]
}

const usePostBulkUploadImages = () => {
  const postBulkUploadImages = async (parameters: BulkImageUploadRequest) => {
    const { brandId, type, files } = parameters

    if (files.length === 0) {
      throw new Error('No files provided for bulk upload')
    }

    const bodyFormData = new FormData()

    // Append all files to FormData with the same key 'files' for bulk upload
    // This allows multiple files to be sent in a single multipart request
    files.forEach((file, index) => {
      bodyFormData.append('files', file)
      console.log(
        `FormData: Appended file ${index + 1}/${files.length} - ${file.name || 'unnamed'} (${file.size} bytes)`
      )
    })

    // Verify FormData has all files
    const formDataEntries = Array.from(bodyFormData.entries())
    const fileEntries = formDataEntries.filter(([key]) => key === 'files')
    console.log(
      `Bulk upload: FormData contains ${fileEntries.length} file entries, sending ${files.length} files of type ${type} in ONE request`
    )

    // In development, use /images/bulk endpoint (proxy will forward to http://localhost:5274/images/bulk)
    // In production, use the full URL
    const endpoint =
      process.env.NODE_ENV === 'development' ? '/images/bulk' : `${process.env.REACT_APP_BASE_URL || ''}/images/bulk`

    return (
      await axiosInstance.post<BulkImageUploadResponse>(endpoint, bodyFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { brandId, type },
      })
    ).data
  }

  return useMutation<BulkImageUploadResponse, AxiosResponse, BulkImageUploadRequest>(
    ['bulk-upload-images'],
    parameters => postBulkUploadImages(parameters)
  )
}

export default usePostBulkUploadImages
